import { createWriteStream } from 'streamsaver';

// Process stores in small batches for memory management
const STORES_PER_WRITE = 250;
const ENCODER = new TextEncoder();

/**
 * Forces garbage collection pause
 * @param {number} ms Milliseconds to wait
 */
async function gcPause(ms = 0) {
  await new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Escapes a value for CSV format
 * @param {string} value The value to escape
 * @returns {string} Escaped value
 */
function escapeCsvValue(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Creates CSV header row
 * @returns {string} CSV header
 */
function createCsvHeader() {
  return [
    'Store URL',
    'Product ID',
    'Product Title',
    'Product Handle',
    'Product URL',
    'Featured Image',
    'Variant ID',
    'Variant Title',
    'Price',
    'Available'
  ].join(',') + '\n';
}

/**
 * Converts a store's products to CSV rows
 * @param {Object} store The store data
 * @returns {string} CSV rows for the store
 */
function storeToCSV(store) {
  let csv = '';
  const storeUrl = escapeCsvValue(store.storeUrl);

  for (const product of store.freeProducts) {
    const productId = escapeCsvValue(product.id);
    const productTitle = escapeCsvValue(product.title.slice(0, 200));
    const productHandle = escapeCsvValue(product.handle);
    const productUrl = escapeCsvValue(product.productUrl);
    const featuredImage = escapeCsvValue(product.featuredImage);

    for (const variant of product.freeVariants) {
      const row = [
        storeUrl,
        productId,
        productTitle,
        productHandle,
        productUrl,
        featuredImage,
        escapeCsvValue(variant.id),
        escapeCsvValue(variant.title.slice(0, 100)),
        escapeCsvValue(variant.price),
        escapeCsvValue(variant.available)
      ].join(',');
      csv += row + '\n';
    }
  }

  return csv;
}

/**
 * Creates a CSV file containing all store data using streaming
 * @param {Object} data The batch data to export
 * @param {Function} onProgress Progress callback (0-100)
 * @returns {Promise<ReadableStream>} Stream of CSV data
 */
export async function exportBatchDataCSV(data, onProgress = () => {}) {
  return new ReadableStream({
    async start(controller) {
      try {
        const results = [...data.results];
        const totalStores = results.length;

        // Write header
        controller.enqueue(ENCODER.encode(createCsvHeader()));

        // Process stores in batches
        for (let i = 0; i < results.length; i += STORES_PER_WRITE) {
          const batchStores = results.slice(i, i + STORES_PER_WRITE);
          
          // Process each store in the batch
          for (const store of batchStores) {
            const storeCsv = storeToCSV(store);
            controller.enqueue(ENCODER.encode(storeCsv));
          }

          // Clear batch reference and allow GC between batches
          batchStores.length = 0;
          await gcPause(10);

          // Report progress
          onProgress((i / totalStores) * 100);
        }

        controller.close();
      } catch (error) {
        console.error('Export error:', error);
        controller.error(new Error('Failed to export data: ' + error.message));
      }
    }
  });
}

/**
 * Creates a JSON file containing all store data using streaming
 * @param {Object} data The batch data to export
 * @param {Function} onProgress Progress callback (0-100)
 * @returns {Promise<ReadableStream>} Stream of JSON data
 */
export async function exportBatchDataJSON(data, onProgress = () => {}) {
  return new ReadableStream({
    async start(controller) {
      try {
        const results = [...data.results];
        const totalStores = results.length;

        // Start JSON array
        controller.enqueue(ENCODER.encode('[\n'));

        // Process stores in batches
        for (let i = 0; i < results.length; i += STORES_PER_WRITE) {
          const batchStores = results.slice(i, i + STORES_PER_WRITE);
          
          // Process each store in the batch
          for (let j = 0; j < batchStores.length; j++) {
            const store = batchStores[j];
            const isLast = i + j === results.length - 1;
            
            // Convert store to JSON and add comma if not last item
            const storeJson = JSON.stringify(store, null, 2) + (isLast ? '\n' : ',\n');
            controller.enqueue(ENCODER.encode(storeJson));
          }

          // Clear batch reference and allow GC between batches
          batchStores.length = 0;
          await gcPause(10);

          // Report progress
          onProgress((i / totalStores) * 100);
        }

        // Close JSON array
        controller.enqueue(ENCODER.encode(']'));
        controller.close();
      } catch (error) {
        console.error('Export error:', error);
        controller.error(new Error('Failed to export data: ' + error.message));
      }
    }
  });
}

/**
 * Downloads the exported data using streaming
 * @param {Object} data The batch data to export
 * @param {Function} onProgress Progress callback
 */
export async function downloadBatchData(data, onProgress = () => {}) {
  try {
    // Get user's preferred export format
    const exportFormat = localStorage.getItem('exportFormat') || 'csv';
    const date = new Date().toISOString().slice(0,10);
    const filename = `freefinder-results-${date}.${exportFormat}`;

    // Create write stream
    const fileStream = createWriteStream(filename, {
      size: 1024 * 1024 * 1024, // Set a large enough size estimate (1GB)
    });

    // Get the readable stream based on format
    const readableStream = await (exportFormat === 'csv' 
      ? exportBatchDataCSV(data, onProgress)
      : exportBatchDataJSON(data, onProgress));

    // Pipe the readable stream to the write stream
    await readableStream.pipeTo(fileStream);

    return true;
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
} 